import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  List,
  Modal,
  message,
  Spin,
  Empty,
  Upload,
  Tag,
  Popconfirm,
  Space,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileOutlined,
  BookOutlined,
  SearchOutlined,
  UploadOutlined,
  EyeOutlined,
  SaveOutlined,
  CloseOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileUnknownOutlined,
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { colors, spacing, radius, typography, shadows } from '../../../styles/monochromeTheme';
import {
  getKnowledgeArticles,
  createKnowledgeArticle,
  updateKnowledgeArticle,
  deleteKnowledgeArticle,
  uploadKnowledgeFile,
  getKnowledgeFileUrl,
  deleteKnowledgeFile,
} from '../../../api/supabase';

const { TextArea } = Input;

// Styles
const styles = {
  page: {
    padding: spacing.xxxxl,
    minHeight: '100vh',
    background: colors.bgPrimary,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xxl,
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `1px solid ${colors.borderLight}`,
    padding: `${spacing.lg} ${spacing.xxl}`,
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: radius.xxl,
    background: colors.bgDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    color: colors.textInverse,
  },
  headerTitle: {
    margin: 0,
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  mainContainer: {
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    gap: spacing.xxl,
    alignItems: 'start',
  },
  sidebar: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xl,
    position: 'sticky',
    top: spacing.xxl,
  },
  articleList: {
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
  },
  articleItem: {
    padding: spacing.md,
    borderRadius: radius.lg,
    marginBottom: spacing.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `1px solid ${colors.borderLight}`,
    background: colors.bgPrimary,
  },
  articleItemActive: {
    background: colors.bgTertiary,
    borderColor: colors.borderMedium,
  },
  articleTitle: {
    fontWeight: typography.fontWeight.semibold,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
  articleDate: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
  },
  contentArea: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
    minHeight: '600px',
  },
  editorContainer: {
    marginTop: spacing.lg,
  },
  primaryBtn: {
    height: '40px',
    borderRadius: radius.lg,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontWeight: typography.fontWeight.semibold,
  },
  secondaryBtn: {
    height: '40px',
    borderRadius: radius.lg,
    background: colors.bgSecondary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },
  fileList: {
    marginTop: spacing.lg,
    padding: spacing.md,
    background: colors.bgTertiary,
    borderRadius: radius.lg,
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.sm,
    background: colors.bgSecondary,
    borderRadius: radius.md,
    marginBottom: spacing.xs,
  },
};

// Quill modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'link', 'image',
  'blockquote', 'code-block'
];

// Get file icon based on extension
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf':
      return <FilePdfOutlined style={{ color: '#e53935' }} />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined style={{ color: '#1976d2' }} />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined style={{ color: '#43a047' }} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return <FileImageOutlined style={{ color: '#f57c00' }} />;
    default:
      return <FileUnknownOutlined />;
  }
};

function KnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editFiles, setEditFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Load articles on mount
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const { data, error } = await getKnowledgeArticles();
      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error loading articles:', error);
      message.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectArticle = (article) => {
    setSelectedArticle(article);
    setIsEditing(false);
  };

  const handleNewArticle = () => {
    setSelectedArticle(null);
    setEditTitle('');
    setEditContent('');
    setEditCategory('');
    setEditFiles([]);
    setIsEditing(true);
  };

  const handleEditArticle = () => {
    if (selectedArticle) {
      setEditTitle(selectedArticle.title || '');
      setEditContent(selectedArticle.content || '');
      setEditCategory(selectedArticle.category || '');
      setEditFiles(selectedArticle.files || []);
      setIsEditing(true);
    }
  };

  const handleSaveArticle = async () => {
    if (!editTitle.trim()) {
      message.warning('Veuillez entrer un titre');
      return;
    }

    setSaving(true);
    try {
      const articleData = {
        title: editTitle.trim(),
        content: editContent,
        category: editCategory.trim(),
        files: editFiles,
      };

      if (selectedArticle) {
        // Update existing
        const { data, error } = await updateKnowledgeArticle(selectedArticle.id, articleData);
        if (error) throw error;
        message.success('Article mis à jour');
        setSelectedArticle(data[0]);
      } else {
        // Create new
        const { data, error } = await createKnowledgeArticle(articleData);
        if (error) throw error;
        message.success('Article créé');
        setSelectedArticle(data[0]);
      }

      setIsEditing(false);
      loadArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!selectedArticle) return;

    try {
      // Delete associated files first
      if (selectedArticle.files && selectedArticle.files.length > 0) {
        for (const file of selectedArticle.files) {
          await deleteKnowledgeFile(file.path);
        }
      }

      const { error } = await deleteKnowledgeArticle(selectedArticle.id);
      if (error) throw error;

      message.success('Article supprimé');
      setSelectedArticle(null);
      setIsEditing(false);
      loadArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name}`;

      const { error } = await uploadKnowledgeFile(file, filename);
      if (error) throw error;

      const url = getKnowledgeFileUrl(filename);

      setEditFiles(prev => [...prev, {
        name: file.name,
        path: filename,
        url: url,
        size: file.size,
        type: file.type,
      }]);

      message.success('Fichier uploadé');
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleRemoveFile = async (fileToRemove) => {
    try {
      await deleteKnowledgeFile(fileToRemove.path);
      setEditFiles(prev => prev.filter(f => f.path !== fileToRemove.path));
      message.success('Fichier supprimé');
    } catch (error) {
      console.error('Error removing file:', error);
      message.error('Erreur lors de la suppression du fichier');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (selectedArticle) {
      // Reset to original values
      setEditTitle(selectedArticle.title || '');
      setEditContent(selectedArticle.content || '');
      setEditCategory(selectedArticle.category || '');
      setEditFiles(selectedArticle.files || []);
    }
  };

  // Filter articles by search
  const filteredArticles = articles.filter(article => {
    const query = searchQuery.toLowerCase();
    return (
      article.title?.toLowerCase().includes(query) ||
      article.content?.toLowerCase().includes(query) ||
      article.category?.toLowerCase().includes(query)
    );
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
          <div style={styles.headerIcon}>
            <BookOutlined />
          </div>
          <div>
            <h1 style={styles.headerTitle}>Base de Connaissances</h1>
            <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
              {articles.length} article{articles.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleNewArticle}
          style={styles.primaryBtn}
        >
          Nouvel Article
        </Button>
      </div>

      {/* Main Content */}
      <div style={styles.mainContainer}>
        {/* Sidebar - Article List */}
        <div style={styles.sidebar}>
          <Input
            placeholder="Rechercher..."
            prefix={<SearchOutlined style={{ color: colors.textMuted }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ marginBottom: spacing.lg, borderRadius: radius.lg }}
          />

          {loading ? (
            <div style={{ textAlign: 'center', padding: spacing.xxl }}>
              <Spin />
            </div>
          ) : filteredArticles.length === 0 ? (
            <Empty
              description="Aucun article"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div style={styles.articleList}>
              {filteredArticles.map(article => (
                <div
                  key={article.id}
                  style={{
                    ...styles.articleItem,
                    ...(selectedArticle?.id === article.id ? styles.articleItemActive : {}),
                  }}
                  onClick={() => handleSelectArticle(article)}
                >
                  <div style={styles.articleTitle}>{article.title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                    {article.category && (
                      <Tag color="blue" style={{ fontSize: typography.fontSize.xs }}>
                        {article.category}
                      </Tag>
                    )}
                    <span style={styles.articleDate}>
                      {formatDate(article.updated_at)}
                    </span>
                  </div>
                  {article.files && article.files.length > 0 && (
                    <div style={{ marginTop: spacing.xs }}>
                      <FileOutlined style={{ color: colors.textMuted, marginRight: spacing.xs }} />
                      <span style={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
                        {article.files.length} fichier{article.files.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          {!selectedArticle && !isEditing ? (
            <div style={{ textAlign: 'center', padding: spacing.xxxxl }}>
              <BookOutlined style={{ fontSize: 64, color: colors.textMuted, marginBottom: spacing.lg }} />
              <p style={{ color: colors.textMuted }}>
                Sélectionnez un article ou créez-en un nouveau
              </p>
            </div>
          ) : isEditing ? (
            // Edit Mode
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.lg }}>
                <h2 style={{ margin: 0 }}>
                  {selectedArticle ? 'Modifier l\'article' : 'Nouvel article'}
                </h2>
                <Space>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancelEdit}
                    style={styles.secondaryBtn}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveArticle}
                    loading={saving}
                    style={styles.primaryBtn}
                  >
                    Sauvegarder
                  </Button>
                </Space>
              </div>

              <Input
                placeholder="Titre de l'article"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{ marginBottom: spacing.md, fontSize: typography.fontSize.lg }}
                size="large"
              />

              <Input
                placeholder="Catégorie (ex: Procédures, Tutoriels, FAQ...)"
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                style={{ marginBottom: spacing.lg }}
              />

              <div style={styles.editorContainer}>
                <ReactQuill
                  theme="snow"
                  value={editContent}
                  onChange={setEditContent}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: '300px', marginBottom: '50px' }}
                />
              </div>

              {/* File Upload Section */}
              <div style={styles.fileList}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
                  <span style={{ fontWeight: typography.fontWeight.semibold }}>
                    <FileOutlined style={{ marginRight: spacing.sm }} />
                    Fichiers attachés
                  </span>
                  <Upload
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={uploading}
                      size="small"
                    >
                      Ajouter un fichier
                    </Button>
                  </Upload>
                </div>

                {editFiles.length === 0 ? (
                  <p style={{ color: colors.textMuted, textAlign: 'center', margin: 0 }}>
                    Aucun fichier attaché
                  </p>
                ) : (
                  editFiles.map((file, index) => (
                    <div key={index} style={styles.fileItem}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        {getFileIcon(file.name)}
                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                          {file.name}
                        </a>
                      </div>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        danger
                        size="small"
                        onClick={() => handleRemoveFile(file)}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            // View Mode
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.lg }}>
                <div>
                  <h2 style={{ margin: 0, marginBottom: spacing.sm }}>{selectedArticle.title}</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                    {selectedArticle.category && (
                      <Tag color="blue">{selectedArticle.category}</Tag>
                    )}
                    <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
                      Mis à jour le {formatDate(selectedArticle.updated_at)}
                    </span>
                  </div>
                </div>
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    onClick={handleEditArticle}
                    style={styles.secondaryBtn}
                  >
                    Modifier
                  </Button>
                  <Popconfirm
                    title="Supprimer cet article?"
                    description="Cette action est irréversible."
                    onConfirm={handleDeleteArticle}
                    okText="Supprimer"
                    cancelText="Annuler"
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                    >
                      Supprimer
                    </Button>
                  </Popconfirm>
                </Space>
              </div>

              <Divider />

              {/* Article Content */}
              <style>{`
                .notion-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
                .notion-content figure { margin: 16px 0; }
                .notion-content figure.image { display: block; }
                .notion-content figure.image a { display: block; }
                .notion-content table { border-collapse: collapse; width: 100%; margin: 12px 0; }
                .notion-content td, .notion-content th { border: 1px solid #e0e0e0; padding: 8px 12px; text-align: left; }
                .notion-content a { color: #1890ff; text-decoration: underline; }
                .notion-content ul, .notion-content ol { padding-left: 24px; }
                .notion-content p { margin: 8px 0; }
                .notion-content [style*="display:contents"] { display: contents; }
              `}</style>
              <div
                className="notion-content ql-editor"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                style={{ padding: 0 }}
              />

              {/* Files Section */}
              {selectedArticle.files && selectedArticle.files.length > 0 && (
                <div style={{ ...styles.fileList, marginTop: spacing.xxl }}>
                  <div style={{ marginBottom: spacing.md }}>
                    <span style={{ fontWeight: typography.fontWeight.semibold }}>
                      <FileOutlined style={{ marginRight: spacing.sm }} />
                      Fichiers attachés ({selectedArticle.files.length})
                    </span>
                  </div>
                  {selectedArticle.files.map((file, index) => (
                    <div key={index} style={styles.fileItem}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        {getFileIcon(file.name)}
                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                          {file.name}
                        </a>
                      </div>
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        size="small"
                        href={file.url}
                        target="_blank"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default KnowledgeBase;
